import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Mail, Phone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { fadeUp } from "../lib/animations";
import { CONTACT, SERVICE_OPTIONS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", company: "", service: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Your inquiry has been sent.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-brand-surface py-24 sm:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-12 gap-10 lg:gap-16">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="col-span-12 lg:col-span-5"
          >
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-ink/40">
              Start a project
            </span>
            <h2 className="mt-4 font-heading font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-brand-ink leading-none">
              Let's make something worth a million.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-brand-ink/60 max-w-md">
              Tell us about your project — training, learning, immersive or
              creative — and we'll get back to you shortly.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-link"
                className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 hover:border-brand transition-colors"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand/10 text-brand">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-brand-ink/40">Email</div>
                  <div className="font-semibold text-brand-ink">{CONTACT.email}</div>
                </div>
                <ArrowUpRight className="ml-auto h-5 w-5 text-brand-ink/30 group-hover:text-brand transition-colors" />
              </a>
              <a
                href={CONTACT.phoneHref}
                data-testid="contact-phone-link"
                className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 hover:border-brand transition-colors"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand/10 text-brand">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-brand-ink/40">Phone</div>
                  <div className="font-semibold text-brand-ink">{CONTACT.phone}</div>
                </div>
                <ArrowUpRight className="ml-auto h-5 w-5 text-brand-ink/30 group-hover:text-brand transition-colors" />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="col-span-12 lg:col-span-7"
          >
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-9"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-widest font-semibold text-brand-ink/50">
                    Name *
                  </label>
                  <Input
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className="mt-2 h-12 border-gray-200 focus-visible:ring-2 focus-visible:ring-brand"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest font-semibold text-brand-ink/50">
                    Email *
                  </label>
                  <Input
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@company.com"
                    className="mt-2 h-12 border-gray-200 focus-visible:ring-2 focus-visible:ring-brand"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest font-semibold text-brand-ink/50">
                    Company
                  </label>
                  <Input
                    data-testid="contact-company-input"
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Company (optional)"
                    className="mt-2 h-12 border-gray-200 focus-visible:ring-2 focus-visible:ring-brand"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest font-semibold text-brand-ink/50">
                    Service
                  </label>
                  <Select
                    value={form.service}
                    onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
                  >
                    <SelectTrigger
                      data-testid="contact-service-select"
                      className="mt-2 h-12 border-gray-200 focus:ring-2 focus:ring-brand"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-5">
                <label className="text-xs uppercase tracking-widest font-semibold text-brand-ink/50">
                  Message *
                </label>
                <Textarea
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your project..."
                  rows={5}
                  className="mt-2 border-gray-200 focus-visible:ring-2 focus-visible:ring-brand resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="mt-7 group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-brand-ink px-8 py-4 text-sm font-semibold text-white hover:bg-brand transition-colors duration-200 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send inquiry
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
